import React, { createContext, useState, useEffect, useContext } from 'react';

export const AccessibilityContext = createContext();

export const AccessibilityProvider = ({ children }) => {
  const [accessibilitySettings, setAccessibilitySettings] = useState(() => {
    // Load saved settings from localStorage
    const saved = localStorage.getItem('accessibility-settings');
    const defaultSettings = {
      blockAnimations: false,
      colorContrast: 'normal', // 'normal', 'uncolored', 'bright', 'inverted'
      textSize: 100, // percentage
      readableFont: false,
      underlineLinks: false,
      underlineHeaders: false,
      imageTitles: false,
      cursorSize: 'normal', // 'normal', 'white', 'black'
      zoomLevel: 100, // percentage
      screenReader: false,
    };
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Force blockAnimations to false if it was accidentally set to true
        return { ...parsed, blockAnimations: false };
      } catch (e) {
        return defaultSettings;
      }
    }
    return defaultSettings;
  });

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('accessibility-settings', JSON.stringify(accessibilitySettings));
    applyAccessibilitySettings(accessibilitySettings);
  }, [accessibilitySettings]);

  const updateSetting = (key, value) => {
    setAccessibilitySettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetSettings = () => {
    const defaultSettings = {
      blockAnimations: false,
      colorContrast: 'normal',
      textSize: 100,
      readableFont: false,
      underlineLinks: false,
      underlineHeaders: false,
      imageTitles: false,
      cursorSize: 'normal',
      zoomLevel: 100,
      screenReader: false,
    };
    setAccessibilitySettings(defaultSettings);
    localStorage.removeItem('accessibility-settings');
  };

  // Apply settings to document
  const applyAccessibilitySettings = (settings) => {
    const root = document.documentElement;

    // Block Animations
    if (settings.blockAnimations) {
      root.classList.add('block-animations');
    } else {
      root.classList.remove('block-animations');
    }

    // Color Contrast
    root.classList.remove('uncolored-display', 'bright-contrast', 'invert-colors');
    if (settings.colorContrast !== 'normal') {
      if (settings.colorContrast === 'uncolored') {
        root.classList.add('uncolored-display');
      } else if (settings.colorContrast === 'bright') {
        root.classList.add('bright-contrast');
      } else if (settings.colorContrast === 'inverted') {
        root.classList.add('invert-colors');
      }
    }

    // Text Size
    root.style.setProperty('--text-scale', settings.textSize / 100);

    // Readable Font
    if (settings.readableFont) {
      root.classList.add('readable-font');
    } else {
      root.classList.remove('readable-font');
    }

    // Underline Links
    if (settings.underlineLinks) {
      root.classList.add('underline-links');
    } else {
      root.classList.remove('underline-links');
    }

    // Underline Headers
    if (settings.underlineHeaders) {
      root.classList.add('underline-headers');
    } else {
      root.classList.remove('underline-headers');
    }

    // Image Titles
    if (settings.imageTitles) {
      root.classList.add('image-titles');
    } else {
      root.classList.remove('image-titles');
    }

    // Cursor Size
    root.classList.remove('big-white-cursor', 'big-black-cursor');
    if (settings.cursorSize === 'white') {
      root.classList.add('big-white-cursor');
    } else if (settings.cursorSize === 'black') {
      root.classList.add('big-black-cursor');
    }

    // Zoom Level
    root.style.setProperty('--zoom-scale', settings.zoomLevel / 100);
  };

  // Screen Reader function with Arabic voice selection
  const readText = (text) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();

      const speakText = () => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'ar-SA'; // Arabic language
        utterance.rate = 0.8; // Slower for better clarity
        utterance.pitch = 1;
        utterance.volume = 1;

        // Get available voices
        const voices = window.speechSynthesis.getVoices();

        // Try to find an Arabic voice (priority order)
        const arabicVoice = voices.find(voice =>
          voice.lang === 'ar-SA' ||
          voice.lang === 'ar_SA'
        ) || voices.find(voice =>
          voice.lang.startsWith('ar')
        ) || voices.find(voice =>
          voice.name.toLowerCase().includes('arabic') ||
          voice.name.toLowerCase().includes('عرب')
        );

        if (arabicVoice) {
          utterance.voice = arabicVoice;
          console.log('Using Arabic voice:', arabicVoice.name, arabicVoice.lang);
        } else {
          console.log('No Arabic voice found, using default voice');
          console.log('Available voices:', voices.map(v => `${v.name} (${v.lang})`));
        }

        window.speechSynthesis.speak(utterance);
      };

      // Voices might not be loaded yet, wait for them
      const voices = window.speechSynthesis.getVoices();
      if (voices.length === 0) {
        window.speechSynthesis.addEventListener('voiceschanged', () => {
          speakText();
        }, { once: true });
      } else {
        speakText();
      }
    } else {
      alert('متصفحك لا يدعم قراءة النصوص. الرجاء استخدام متصفح حديث مثل Chrome أو Edge.');
    }
  };

  const readSelectedText = () => {
    const selectedText = window.getSelection().toString().trim();
    if (selectedText) {
      readText(selectedText);
      return true;
    }
    return false;
  };

  const stopReading = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  };

  const value = {
    accessibilitySettings,
    updateSetting,
    resetSettings,
    readText,
    readSelectedText,
    stopReading,
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};
